import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import {
  Grid,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { showModal, userDataReducer } from "../../../redux/counterSlice";
import { useAdmin } from "../../../components/Admin/Admin";
import { FormInputText } from "../../../components/form-components/FormInputText";
import { dispatchLoginSchema } from "../../../app/schema";

function LoginDispatch() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const admin = useAdmin();
  const setAdminContext = admin?.login;

  const [dispatchList, setDispatchList] = useState([]);
  const [loadingDispatch, setLoadingDispatch] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(dispatchLoginSchema),
    defaultValues: {
      dispatch_team_id: "",
      password: "",
    },
  });

  useEffect(() => {
    sessionStorage.setItem("lastLoginPath", window.location.pathname);
    fetchDispatchTeams();
  }, []);

  const fetchDispatchTeams = async () => {
    try {
      setLoadingDispatch(true);
      const res = await axios.get("/getAllActiveDispatchTeams");
      setDispatchList(res?.data?.data || []);
    } catch (error) {
      toast.error("Failed to load dispatch list");
    } finally {
      setLoadingDispatch(false);
    }
  };

  const login = async (data) => {
    const selectedTeam = dispatchList.find(
      (item) => String(item.dispatch_team_id) === String(data.dispatch_team_id)
    );

    const payload = {
      dispatch_team_id: data.dispatch_team_id,
      dispatch_name: selectedTeam?.dispatch_name,
      location_details_id: selectedTeam?.location_details_id,
      password: data.password,
    };

    try {
      const response = await axios.post("/loginDispatch", payload);
      const res = response?.data;

      if (!res?.error) {
        const token = res?.data?.token;
        if (!token) throw new Error("Token not received");

        const locationDetailsId =
          res?.data?.location_details_id || selectedTeam?.location_details_id;
        const loc =
          res?.data?.location?.[0] ||
          (Array.isArray(selectedTeam?.location_details)
            ? selectedTeam.location_details[0]
            : selectedTeam?.location_details);

        let regionId = null;
        if (loc?.region_id) {
          regionId = loc.region_id;
        } else if (Array.isArray(loc?.region) && loc.region.length > 0) {
          regionId = loc.region[0]?.region_id;
        } else if (loc?.region?.region_id) {
          regionId = loc.region.region_id;
        }

        const storageData = {
          token,
          loggedIn: true,
          dispatch_team_id: res?.data?.dispatch_team_id || data.dispatch_team_id,
          dispatch_name: res?.data?.dispatch_name || selectedTeam?.dispatch_name,
          location_details_id: locationDetailsId,
          region_id: regionId || null,
        };

        // Store in sessionStorage
        sessionStorage.setItem("userData", JSON.stringify(storageData));
        sessionStorage.setItem("loginType", "dispatch");
        sessionStorage.setItem("dispatch_name", storageData.dispatch_name);
        sessionStorage.setItem(
          "dispatch_team_id",
          String(storageData.dispatch_team_id)
        );
        if (locationDetailsId) {
          sessionStorage.setItem(
            "location_details_id",
            String(locationDetailsId)
          );
        }
        if (regionId) {
          sessionStorage.setItem("region_id", String(regionId));
        }

        dispatch(userDataReducer(storageData));

        if (typeof setAdminContext === "function") {
          setAdminContext(token);
        }

        navigate("/dashboard");
        toast.success("Logged in successfully");
      } else {
        dispatch(
          showModal({ msg: res?.message || "Login failed", error: true })
        );
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.response?.data?.message || error?.message || "Server error",
          error: true,
        })
      );
    }
  };

  const paperStyle = {
    padding: 20,
    maxWidth: 400,
    width: "100%",
  };

  const btnstyle = { margin: "8px 0", background: "#E63D24" };

  return (
    <Box
      className="login-page"
      sx={{
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Paper elevation={10} style={paperStyle} className="bg-light">
        <form onSubmit={handleSubmit(login)}>
          <Grid container spacing={2}>
            <Grid item xs={12} align="center">
              <img
                src={require("../../../assets/logo.png")}
                alt="Logo"
                width={200}
              />
            </Grid>

            {/* Dispatch Team Dropdown */}
            <Grid item xs={12}>
              <Controller
                name="dispatch_team_id"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.dispatch_team_id}>
                    <InputLabel id="dispatch-label">Dispatch Team</InputLabel>
                    <Select
                      {...field}
                      labelId="dispatch-label"
                      label="Dispatch Team"
                    >
                      {dispatchList?.map((item) => {
                        const loc = Array.isArray(item.location_details)
                          ? item.location_details[0]
                          : item.location_details;
                        const locName = loc?.location_name
                          ? ` - ${loc.location_name}`
                          : "";
                        return (
                          <MenuItem
                            key={item.dispatch_team_id}
                            value={item.dispatch_team_id}
                          >
                            {item.dispatch_name}
                            {locName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    <FormHelperText>
                      {errors?.dispatch_team_id?.message}
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12}>
              <FormInputText
                name="password"
                control={control}
                label="Password"
                errors={errors}
                type="password"
              />
            </Grid>

            {/* Submit */}
            <Grid item xs={12}>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                style={btnstyle}
                fullWidth
                disabled={loadingDispatch}
              >
                Login
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default LoginDispatch;
